<template>
    <rkm-modal
        extra-large
        ref="Modal"
        title="Solicitações"
    >
        <rkm-form
            v-if="solicitacoesData"
            ref="Form"
        >
            <rkm-area
                title="Solicitações"
                no-margin-top
            >
                <div
                    v-if="solicitacoesData.model.solicitacoesItens.length > 0"
                    class="mt-2 mb-3"
                >
                    <rkm-button
                        v-if="solicitacoesData.model.solicitacoesItens.find(i => !i.checked)"
                        @click="selectAll"
                        color="primary"
                    >
                        Selecionar todos
                    </rkm-button>

                    <rkm-button
                        v-else
                        @click="unselectAll"
                        color="primary"
                    >
                        Desselecionar todos
                    </rkm-button>

                    <rkm-button
                        v-if="solicitacoesData.model.solicitacoesItens.some(i => i.checked)"
                        @click="printChecked"
                        color="info"
                    >
                        Imprimir selecionados
                    </rkm-button>
                </div>

                <rkm-table
                    :fields="fields.solicitacoes"
                    :items="solicitacoesData.model.solicitacoesItens"
                    hide-filters
                    hide-pagination
                    sort-by="dataSolicitacao"
                    sort-desc
                >
                    <template v-slot:cell(checkbox)="{ item }">
                        <rkm-form-checkbox v-model="item.checked" />
                    </template>

                    <template v-slot:cell(status)="{ item }">
                        <rkm-form-select
                            v-if="item.isExternoNaoRegulado"
                            :options="solicitacoesData.form.status.filter(s => item.statusPermitidos.includes(s.id))"
                            :validations="getItemValidations(item).status"
                            name="Status"
                            text-field="nome"
                            v-model="item.status"
                            value-field="id"
                        />

                        <template v-else>{{ item.status }}</template>
                    </template>

                    <template v-slot:cell(dataAgendada)="{ item }">
                        <rkm-form-date-picker
                            v-if="item.isExternoNaoRegulado"
                            :min-date="solicitacoesData.additionals.dataAtual"
                            :validations="getItemValidations(item).dataAgendada"
                            name="Data agendada"
                            v-model="item.dataAgendada"
                        />

                        <template v-else>{{ item.dataAgendada ? formatDate(item.dataAgendada) : null }}</template>
                    </template>

                    <template v-slot:cell(unidadeExecutora)="{ item }">
                        <rkm-form-select
                            v-if="item.isExternoNaoRegulado"
                            :options="solicitacoesData.form.unidadesExecutoras"
                            :validations="getItemValidations(item).unidadeExecutora"
                            name="Unidade executora"
                            show-default-option
                            text-field="nomeFantasia"
                            v-model="item.unidadeExecutora"
                            value-field="id"
                        />

                        <template v-else>{{ item.unidadeExecutora }}</template>
                    </template>

                    <template v-slot:cell(ocupacaoExecutora)="{ item }">
                        <rkm-form-input-autocomplete
                            v-if="item.isExternoNaoRegulado"
                            :repository="repositories.ocupacao"
                            :request-query-string-params="{ procedimento_id: item.procedimentoId }"
                            :search-fields="[
                                {key: 'ocupacao.codigo', searchable: true},
                                {key: 'ocupacao.nome', searchable: true},
                            ]"
                            :text-field="(item) => item.codigo"
                            :validations="getItemValidations(item).ocupacaoExecutora"
                            name="CBO executora"
                            class="mb-2"
                            v-model="item.ocupacaoExecutora"
                        >
                            <template #itemLabel="{ item }">
                                {{ item.codigo ? `${item.codigo} - ${item.nome}` : null }}
                            </template>
                        </rkm-form-input-autocomplete>

                        <template v-else>{{ item.ocupacaoExecutora }}</template>
                    </template>

                    <template v-slot:cell(usuarioExecutor)="{ item }">
                        <rkm-form-input
                            v-if="item.isExternoNaoRegulado"
                            :validations="getItemValidations(item).usuarioExecutor"
                            name="Profissional executor"
                            uppercase
                            v-model="item.usuarioExecutor"
                        />

                        <template v-else>{{ item.usuarioExecutor }}</template>
                    </template>

                    <template v-slot:cell(actions)="{ item }">
                        <rkm-button
                            @click="$refs.ModalDetalhe.open(item)"
                            color="primary"
                            small
                            title="Exibir detalhes"
                        >
                            <rkm-icon icon="bars" />
                        </rkm-button>

                        <rkm-button
                            @click="print(item)"
                            color="info"
                            small
                            title="Imprimir"
                        >
                            <rkm-icon icon="print" />
                        </rkm-button>
                    </template>
                </rkm-table>
            </rkm-area>

            <rkm-area title="Procedimentos realizados previamente">
                <template slot="trailing">
                    <rkm-button
                        v-if="!acompanhamentoOci.isConcluido"
                        @click="$refs.ModalProcedimentoRealizado.open()"
                        color="primary"
                        small
                        title="Adicionar procedimento"
                    >
                        <rkm-icon icon="plus" />
                    </rkm-button>
                </template>

                <rkm-table
                    :fields="fields.procedimentosRealizados"
                    :items="solicitacoesData.model.procedimentosRealizados"
                    hide-filters
                    hide-pagination
                    sort-by="procedimentoDescricao"
                >
                    <template v-slot:cell(dataExecutada)="{ item }">
                        <rkm-form-date-picker
                            :max-date="solicitacoesData.additionals.dataAtual"
                            :validations="validations.procedimentoRealizado.dataExecutada"
                            name="Data executada"
                            v-model="item.dataExecutada"
                        />
                    </template>

                    <template v-slot:cell(unidadeExecutora)="{ item }">
                        <rkm-form-select
                            :options="solicitacoesData.form.unidadesExecutoras"
                            :validations="validations.procedimentoRealizado.unidadeExecutora"
                            name="Unidade executora"
                            show-default-option
                            text-field="nomeFantasia"
                            v-model="item.unidadeExecutoraId"
                            value-field="id"
                        />
                    </template>

                    <template v-slot:cell(ocupacaoExecutora)="{ item }">
                        <rkm-form-input-autocomplete
                            :repository="repositories.ocupacao"
                            :request-query-string-params="{ procedimento_id: item.procedimentoId }"
                            :search-fields="[
                                {key: 'ocupacao.codigo', searchable: true},
                                {key: 'ocupacao.nome', searchable: true},
                            ]"
                            :text-field="(item) => item.codigo"
                            :validations="validations.procedimentoRealizado.ocupacaoExecutora"
                            name="CBO executora"
                            class="mb-2"
                            v-model="item.ocupacaoExecutora"
                        >
                            <template #itemLabel="{ item }">
                                {{ item.codigo ? `${item.codigo} - ${item.nome}` : null }}
                            </template>
                        </rkm-form-input-autocomplete>
                    </template>

                    <template v-slot:cell(usuarioExecutor)="{ item }">
                        <rkm-form-input
                            :validations="validations.procedimentoRealizado.usuarioExecutor"
                            name="Profissional executor"
                            uppercase
                            v-model="item.usuarioExecutorNome"
                        />
                    </template>

                    <template v-slot:cell(actions)="{ item }">
                        <rkm-button
                            :disabled="!!item.id"
                            @click="destroyProcedimentoRealizado(item)"
                            color="danger"
                            small
                            title="Excluir"
                        >
                            <rkm-icon icon="trash" />
                        </rkm-button>
                    </template>
                </rkm-table>
            </rkm-area>

            <ModalProcedimentoRealizado
                :procedimentos-incluidos="solicitacoesData.model.procedimentosRealizados"
                :procedimentos="solicitacoesData.form.procedimentosOci"
                ref="ModalProcedimentoRealizado"
            />
        </rkm-form>

        <ModalDetalhe ref="ModalDetalhe" />

        <template v-slot:footer="{cancel}">
            <rkm-button
                :disabled="!!loading"
                @click="cancel"
                color="secondary"
            >
                Fechar
            </rkm-button>

            <rkm-button
                v-if="solicitacoesData && (solicitacoesData.model.solicitacoesItens.some(s => s.isExternoNaoRegulado)
                    || solicitacoesData.model.procedimentosRealizados.length > 0)"
                :disabled="!!loading"
                @click="$refs.Form.validate(updateSolicitacoes)"
                color="primary"
            >
                Salvar
            </rkm-button>
        </template>
    </rkm-modal>
</template>

<script>
// Components.
import ModalDetalhe from './components/detalhe/Modal'
import ModalProcedimentoRealizado from './components/procedimento-realizado/Modal'

// Vuex.
import { mapActions, mapGetters } from 'vuex'

// Mixins.
import { repository, error, formats, file, stringCase } from '@/mixins'

// Moment.
import moment from 'moment'

// Enums.
import soapSolicitacaoExameItemStatus from '@/enums/ambulatorio/soapSolicitacaoExameItemStatus'
import procedimento from '@/enums/sigtap/procedimento'

export default {
    components: { ModalDetalhe, ModalProcedimentoRealizado },
    computed: {
        ...mapGetters({ loading: 'getLoading' }),
        fields() {

            return {
                solicitacoes: [
                    { key: 'checkbox', label: '#', class: 'col-min text-center' },
                    { key: 'dataSolicitacao', label: 'Data da solicitação', formatter: v => this.formatDate(v), sortable: true },
                    { key: 'exameDescricao', label: 'Exame', sortable: true },
                    { key: 'status', label: 'Status' },
                    { key: 'dataAgendada', label: 'Data agendada' },
                    { key: 'unidadeExecutora', label: 'Unidade executora' },
                    { key: 'ocupacaoExecutora', label: 'CBO executora' },
                    { key: 'usuarioExecutor', label: 'Profissional executor' },
                    { key: 'actions', label: 'Ações', class: 'col-min text-center' },
                ],
                procedimentosRealizados: [
                    { key: 'procedimentoDescricao', label: 'Procedimento', sortable: true },
                    { key: 'dataExecutada', label: 'Data executada' },
                    { key: 'unidadeExecutora', label: 'Unidade executora' },
                    { key: 'ocupacaoExecutora', label: 'CBO executora' },
                    { key: 'usuarioExecutor', label: 'Profissional executor' },
                    { key: 'actions', label: 'Ações', class: 'col-min text-center' },
                ]
            }
        },
        validations() {

            return {
                procedimentoRealizado: {
                    dataExecutada: { required: true },
                    unidadeExecutora: { required: true },
                    ocupacaoExecutora: { required: true },
                    usuarioExecutor: { max: 100 },
                }
            }
        },
        examesCitopatologicosColoUtero() {

            return [
                this.enums.procedimento.COLETA_DE_MATERIAL_PARA_EXAME_CITOPATOLOGICO_DE_COLO_DE_UTERO,
                this.enums.procedimento.EXAME_CITOPATOLOGICO_CERVICO_VAGINAL_MICROFLORA,
                this.enums.procedimento.EXAME_CITOPATOLOGICO_CERVICO_VAGINAL_MICROFLORA_RASTREAMENTO,
            ]
        },
        examesCitopatologicosMama() {

            return [
                this.enums.procedimento.EXAME_CITOPATOLOGICO_DE_MAMA,
            ]
        },
        examesMamografia() {

            return [
                this.enums.procedimento.MAMOGRAFIA,
                this.enums.procedimento.MAMOGRAFIA_BILATERAL_PARA_RASTREAMENTO
            ]
        }
    },
    created() {
        this.repositories = {
            monitoramentoProtocolo: this.getConectaSusRepository('regulacao.monitoramentoProtocolo'),
            ocupacao: this.getConectaSusRepository('sigtap.ocupacao'),
            impressaoAmbulatorio: this.getConectaSusRepository('impressao.ambulatorio'),
        }
    },
    data: () => ({
        acompanhamentoOci: null,
        solicitacoesData: null,
        enums: { soapSolicitacaoExameItemStatus, procedimento },
        others: { moment }
    }),
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        getItemValidations(item) {

            const isStatusAgendamento = item.status === this.enums.soapSolicitacaoExameItemStatus.AGENDADO
                || item.status === this.enums.soapSolicitacaoExameItemStatus.REAGENDADO

            const isStatusRealizado = item.status === this.enums.soapSolicitacaoExameItemStatus.REALIZADO

            return {
                status: { required: true },
                dataAgendada: { required: isStatusAgendamento || isStatusRealizado },
                unidadeExecutora: { required: isStatusAgendamento || isStatusRealizado },
                ocupacaoExecutora: { required: isStatusAgendamento || isStatusRealizado },
                usuarioExecutor: { max: 100 },
            }
        },
        open(item) {

            this.acompanhamentoOci = item

            this.solicitacoesData = null

            this.load()

            this.$refs.Modal.show()
        },
        load() {

            this.setLoading()

            this.repositories.monitoramentoProtocolo.getSolicitacoes(this.acompanhamentoOci.id)
                .then(({ data }) => {

                    data.model.solicitacoesItens.forEach(item => {

                        item.checked = false
                    })

                    this.solicitacoesData = data
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        updateSolicitacoes() {

            this.setLoading()

            this.repositories.monitoramentoProtocolo.updateSolicitacoes(this.acompanhamentoOci.id, this.prepareData())
                .then(() => {

                    this.showSaveNotification()
                    this.load()
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        prepareData() {

            const solicitacoes = this.solicitacoesData.model.solicitacoesItens.filter(s => s.isExternoNaoRegulado).map(item => {

                return {
                    id: item.id,
                    statusId: item.status,
                    dataAgendada: item.dataAgendada ? this.others.moment(item.dataAgendada).format('DD/MM/YYYY') : null,
                    unidadeExecutoraId: item.unidadeExecutora,
                    ocupacaoExecutoraId: item.ocupacaoExecutora ? item.ocupacaoExecutora.id : null,
                    usuarioExecutorNome: item.usuarioExecutor,
                }
            })

            const procedimentosRealizados = this.solicitacoesData.model.procedimentosRealizados.map(item => {

                return {
                    id: item.id,
                    procedimentoId: item.procedimentoId,
                    dataExecutada: item.dataExecutada ? this.others.moment(item.dataExecutada).format('DD/MM/YYYY') : null,
                    unidadeExecutoraId: item.unidadeExecutoraId,
                    ocupacaoExecutoraId: item.ocupacaoExecutora ? item.ocupacaoExecutora.id : null,
                    usuarioExecutorNome: item.usuarioExecutorNome,
                }
            })

            return this.camelObjectKeysToSnakeRecursive({ solicitacoes, procedimentosRealizados })
        },
        print(item) {

            const params = { solicitacao_exame_item_id: item.id }

            if (this.examesCitopatologicosColoUtero.includes(item.exameCodigo)) {

                this.setLoading()

                this.repositories.impressaoAmbulatorio.requisicaoExameCitopatologicoColoUtero(item.atendimentoId, params)
                    .then(({data, headers}) => this.openFile(data, headers['content-type']))
                    .catch(this.handleError)
                    .finally(() => this.unsetLoading())
            }
            else if (this.examesCitopatologicosMama.includes(item.exameCodigo)) {

                this.setLoading()

                this.repositories.impressaoAmbulatorio.requisicaoExameCitopatologicoMama(item.atendimentoId, params)
                    .then(({data, headers}) => this.openFile(data, headers['content-type']))
                    .catch(this.handleError)
                    .finally(() => this.unsetLoading())
            }
            else if (this.examesMamografia.includes(item.exameCodigo)) {

                this.setLoading()

                this.repositories.impressaoAmbulatorio.requisicaoMamografia(item.atendimentoId, params)
                    .then(({data, headers}) => this.openFile(data, headers['content-type']))
                    .catch(this.handleError)
                    .finally(() => this.unsetLoading())
            }
            else if (item.isExameLaboratorial) {

                this.setLoading()

                this.repositories.impressaoAmbulatorio.servicoAnaliseDiagnosticoTerapiaSolicitacaoExame(item.atendimentoId, params)
                    .then(({data, headers}) => this.openFile(data, headers['content-type']))
                    .catch(this.handleError)
                    .finally(() => this.unsetLoading())
            }
            else {

                if (item.isAltaComplexidade) {

                    this.setLoading()

                    this.repositories.impressaoAmbulatorio.laudoSolicitacaoAutorizacaoProcedimentoAmbulatorial(item.atendimentoId, params)
                        .then(({data, headers}) => this.openFile(data, headers['content-type']))
                        .catch(this.handleError)
                        .finally(() => this.unsetLoading())
                }
                else {

                    this.setLoading()

                    this.repositories.impressaoAmbulatorio.fichaRequisicaoServicoAuxiliarDiagnoseTerapia(item.atendimentoId, params)
                        .then(({data, headers}) => this.openFile(data, headers['content-type']))
                        .catch(this.handleError)
                        .finally(() => this.unsetLoading())
                }
            }
        },
        destroyProcedimentoRealizado(item) {

            this.showDeleteConfirmation(
                'Deseja realmente excluir o procedimento?',
                async () => {

                    this.solicitacoesData.model.procedimentosRealizados.splice(
                        this.solicitacoesData.model.procedimentosRealizados.indexOf(item),
                        1
                    )
                }
            )
        },
        selectAll() {

            this.solicitacoesData.model.solicitacoesItens.forEach(item => {

                item.checked = true
            })
        },
        unselectAll() {

            this.solicitacoesData.model.solicitacoesItens.forEach(item => {

                item.checked = false
            })
        },
        printChecked() {

            this.solicitacoesData.model.solicitacoesItens.filter(i => (i.checked || false)).forEach(item => {

                this.print(item)
            })
        }
    },
    mixins: [repository, error, formats, file, stringCase],
}
</script>